import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ResponsiveImage } from '../../../../components';
import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';
import {
  abbreviateName,
  createPostBodyHelper,
  getInitialValuesForEditPost,
  transformRawContent,
} from '../../../../util/circleHelper';
import css from './CreatePostBar.module.css';
import CreatePostForm from '../CreatePostForm/CreatePostForm';
import { manageDisableScrolling } from '../../../../ducks/ui.duck';
import {
  clearSpaceTopics,
  getSpaceTopics,
  toggelCoverImageReducer,
  toggelDocumentReducer,
  toggelGiphyReducer,
  toggelImageReducer,
  toggelVideoReducer,
  toggleEditPost,
} from '../../CircleCommunityPage.duck';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { CompositeDecorator, ContentState, EditorState, Modifier, convertToRaw } from 'draft-js';
import { reinitilizeIntercomOnClickEvents } from '../../../../components/IntercomComponent/IntercomComponent';
import { getCommunityUserRole } from '../../../../util/data';
import { FormattedMessage } from '../../../../util/reactIntl';

const mentionStrategy = (contentBlock, callback, contentState) => {
  const text = contentBlock.getText();
  let matchArr, start;
  const regex = /@\w+/g; // Detect mentions starting with '@'
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};

const Mention = props => (
  <span style={{ backgroundColor: '#e8f5ff', color: '#0073e6' }}>{props.children}</span>
);

const compositeDecorator = new CompositeDecorator([
  {
    strategy: mentionStrategy,
    component: Mention,
  },
]);

const getTextFromTiptapContent = content => {
  if (!Array.isArray(content)) {
    return '';
  }

  const getInlineText = item => {
    if (item?.type === 'hardBreak') {
      return '\n';
    }

    return item?.text || item?.circle_ios_fallback_text || item?.attrs?.label || '';
  };

  return content
    .map(block => {
      if (!Array.isArray(block?.content)) {
        return '';
      }

      return block.content.map(getInlineText).join('');
    })
    .filter(Boolean)
    .join('\n');
};

const CreatePostBar = props => {
  const {
    currentCommunityMember,
    spaceListData,
    onCreateSpacePost,
    requestDirectUpload,
    isCreatePost = false,
    onToggleCreatePost,
    composerMessageId = 'CircleFeedPage.startPost',
    groupPageVariant = false,
    onGiphyChange,
    onDocumentClick,
    onImageClick,
    onVideoClick,
    isGiphyOpen,
    isDocumentModal,
    isImageModal,
    isVideoUpload,
    editPostData,
    onFetchTopics,
    spaceTopicsData,
    advancedSearchData,
    onAdvancedSearch,
    params,
    onClearSpaceTopics,
    isCoverImageModal,
    onManageDisableScrolling,
    onCoverImageClick,
    currentUser,
    requestCreatePost,
    errorCreatePost,
    hideComposer = false,
  } = props;

  const postId = editPostData?.id || null;
  const dispatch = useDispatch();

  const { userModeratorsGroups } = useSelector(state => state.user);
  // Editor State for Draft js

  const [editorState, setEditorState] = useState(EditorState.createEmpty(compositeDecorator));
  const editorStateRef = useRef(editorState);
  const updateEditorState = useCallback(nextEditorState => {
    editorStateRef.current = nextEditorState;
    setEditorState(nextEditorState);
  }, []);
  // const [formateEditor, setFormateEditor] = useState(false);
  const [formOldValues, setFormOldValues] = useState(() =>
    getInitialValuesForEditPost(editPostData)
  );
  useEffect(() => {
    editorStateRef.current = editorState;
  }, [editorState]);

  useEffect(() => {
    const initialValues = getInitialValuesForEditPost(editPostData);
    setFormOldValues(() => initialValues);

    if (editPostData?.id) {
      const initialText =
        getTextFromTiptapContent(editPostData?.tiptap_body?.body?.content) ||
        editPostData?.body_plain_text ||
        editPostData?.body ||
        initialValues?.postDescription ||
        '';
      if (initialValues?.spaceId) {
        onFetchTopics(initialValues.spaceId);
      }
      updateEditorState(
        initialText
          ? EditorState.createWithContent(
              ContentState.createFromText(initialText),
              compositeDecorator
            )
          : EditorState.createEmpty(compositeDecorator)
      );
    }
  }, [editPostData, updateEditorState]);

  const { avatar_url } = currentCommunityMember || {};

  const [searchTerm, setSearchTerm] = useState('');
  const [isEmoji, setIsEmoji] = useState(false);

  // Close modal when post creation finishes successfully: detect transition true -> false
  const prevRequestCreatePostRef = useRef(requestCreatePost);
  useEffect(() => {
    const prev = prevRequestCreatePostRef.current;
    if (prev && !requestCreatePost && isCreatePost && !errorCreatePost) {
      updateEditorState(EditorState.createEmpty(compositeDecorator));
      onToggleCreatePost();
      dispatch(toggleEditPost({}));
    }
    prevRequestCreatePostRef.current = requestCreatePost;
  }, [
    dispatch,
    errorCreatePost,
    isCreatePost,
    onToggleCreatePost,
    requestCreatePost,
    updateEditorState,
  ]);

  const handlePostCreation = async values => {
    const pollPayload = values?.__pollPayload || null;
    const pollClientRequestId = values?.__pollClientRequestId || null;
    const pollDescription = String(pollPayload?.description || '').trim();
    const contentState = pollPayload
      ? ContentState.createFromText(pollDescription)
      : editorStateRef.current?.getCurrentContent();
    const rawContent = convertToRaw(contentState); // Convert to raw JS object
    const formateEditor = transformRawContent(rawContent);
    const valuesForPost = pollPayload
      ? {
          ...values,
          postTitle: pollPayload.title || (pollDescription ? '' : 'Poll'),
          postDescription: pollDescription,
          image: [],
          imagefiles: [],
          gifs: [],
          giffiles: [],
          video: [],
          videofiles: [],
          document: [],
          documentsfiles: [],
          coverImage: null,
          coverImagefiles: null,
        }
      : values;
    const apiParams = createPostBodyHelper(
      valuesForPost,
      formateEditor,
      currentUser,
      currentCommunityMember,
      rawContent,
      spaceListData
    );
    if (pollPayload) {
      apiParams.poll = pollPayload;
      apiParams.clientRequestId = pollClientRequestId;
    }

    const response = await onCreateSpacePost(apiParams, values.spaceId, postId);
    // Rely on requestCreatePost transition effect to close the modal
  };

  const handleCloseModal = () => {
    // Prevent closing modal while post is being created
    if (requestCreatePost) {
      return;
    }
    onClearSpaceTopics();
    onToggleCreatePost();
    dispatch(toggleEditPost({}));
    updateEditorState(EditorState.createEmpty(compositeDecorator));
  };

  const insertEmoji = emoji => {
    const currentEditorState = editorStateRef.current;
    const currentContent = currentEditorState.getCurrentContent();
    const selection = currentEditorState.getSelection();
    const newContentState = Modifier.replaceText(currentContent, selection, emoji);
    const newEditorState = EditorState.forceSelection(
      EditorState.push(currentEditorState, newContentState, 'insert-characters'),
      newContentState.getSelectionAfter()
    );
    updateEditorState(newEditorState);
    setIsEmoji(false);
  };

  const handleOpenComposer = () => onToggleCreatePost(true);

  const avatarContent = avatar_url ? (
    <ResponsiveImage imagewithURL={avatar_url} alt="create_post_avatar_url" />
  ) : currentCommunityMember?.name ? (
    <div className={css.nameWrapper}>{abbreviateName(currentCommunityMember?.name)}</div>
  ) : null;

  return (
    <>
      {!hideComposer && groupPageVariant ? (
        <div className={css.createpostbar}>
          <button
            type="button"
            className={css.groupPageComposer}
            onClick={handleOpenComposer}
            aria-label="Start a post"
          >
            <div className={css.composerAvatar}>{avatarContent}</div>
            <span className={css.composerPlaceholder}>
              <FormattedMessage id={composerMessageId} />
            </span>
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 2C12.3009 2.04488 8.76597 3.53429 6.15013 6.15013C3.53429 8.76597 2.04488 12.3009 2 16C2.04488 19.6991 3.53429 23.234 6.15013 25.8499C8.76597 28.4657 12.3009 29.9551 16 30C19.6991 29.9551 23.234 28.4657 25.8499 25.8499C28.4657 23.234 29.9551 19.6991 30 16C29.9551 12.3009 28.4657 8.76597 25.8499 6.15013C23.234 3.53429 19.6991 2.04488 16 2ZM24 17H17V24H15V17H8V15H15V8H17V15H24V17Z"
                fill="#2F0F48"
              />
            </svg>
          </button>
        </div>
      ) : !hideComposer ? (
        <button
          type="button"
          className={css.createPostWrapper}
          onClick={handleOpenComposer}
          aria-label="Start a post"
        >
          <div className={css.imageleft}>
            <div className={css.imageContainer}>{avatarContent}</div>
            <span className={css.startHeading}>
              <FormattedMessage id={composerMessageId} />
            </span>
          </div>
          <ProfileIconCard type="add" />
        </button>
      ) : null}
      {/* create a modal here which will open onclick of create a post button*/}
      {isCreatePost ? (
        <Modal
          className={css.createPostModal}
          id={'createPostModal'}
          isOpen={isCreatePost}
          usePortal={true}
          onClose={() => {
            handleCloseModal();
            if (currentUser) {
              reinitilizeIntercomOnClickEvents(currentUser);
            }
          }}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <CreatePostForm
            currentUser={currentUser}
            editorState={editorState}
            setEditorState={updateEditorState}
            insertEmoji={insertEmoji}
            initialValues={formOldValues}
            formOldValues={formOldValues}
            setFormOldValues={setFormOldValues}
            onSubmit={handlePostCreation}
            spaceListData={spaceListData}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isEmoji={isEmoji}
            setIsEmoji={setIsEmoji}
            requestDirectUpload={requestDirectUpload}
            onGiphyChange={onGiphyChange}
            onDocumentClick={onDocumentClick}
            onImageClick={onImageClick}
            onVideoClick={onVideoClick}
            isGiphyOpen={isGiphyOpen}
            isDocumentModal={isDocumentModal}
            isImageModal={isImageModal}
            isVideoUpload={isVideoUpload}
            onFetchTopics={onFetchTopics}
            spaceTopicsData={spaceTopicsData}
            advancedSearchData={advancedSearchData}
            onAdvancedSearch={onAdvancedSearch}
            params={params}
            isCoverImageModal={isCoverImageModal}
            onCoverImageClick={onCoverImageClick}
            userModeratorsGroups={userModeratorsGroups}
            requestCreatePost={requestCreatePost}
            isEditMode={Boolean(postId)}
          />
        </Modal>
      ) : null}
    </>
  );
};

const mapStateToProps = state => {
  const {
    isGiphyOpen,
    isDocumentModal,
    isImageModal,
    isVideoUpload,
    editPostData,
    requestSpaceTopics,
    spaceTopicsData,
    isCoverImageModal,
    requestCreatePost,
    errorCreatePost,
  } = state.CircleCommunityPage;

  return {
    isGiphyOpen,
    isDocumentModal,
    isImageModal,
    isVideoUpload,
    editPostData,
    requestSpaceTopics,
    spaceTopicsData,
    isCoverImageModal,
    requestCreatePost,
    errorCreatePost,
  };
};
const mapDispatchToProps = dispatch => ({
  onGiphyChange: () => dispatch(toggelGiphyReducer()),
  onDocumentClick: () => dispatch(toggelDocumentReducer()),
  onCoverImageClick: () => dispatch(toggelCoverImageReducer()),
  onImageClick: () => dispatch(toggelImageReducer()),
  onVideoClick: () => dispatch(toggelVideoReducer()),
  onFetchTopics: spaceId => dispatch(getSpaceTopics(spaceId)),
  onClearSpaceTopics: () => dispatch(clearSpaceTopics()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});
export default compose(connect(mapStateToProps, mapDispatchToProps))(CreatePostBar);
